import { useEffect, useRef } from 'react';

// Event type definitions
interface UpdateQuoteOfferingQtyData {
  /**
   * Source offering that triggered the event
   * This allows knowledge to determine if the event should be processed
   */
  sourceOffering: {
    quoteOfferingId: string;
    parentQuoteOfferingId: string | null;
    startDate: Date;
    endDate: Date;
  };
  /**
   * Offerings that should be checked for updates
   */
  scopedOfferingIds: Set<string>;
  /**
   * Products within scoped offerings that should be updated
   */
  targetProductIds: Set<string>;
  /**
   * Quantity to set for the target products
   */
  quantity: number;
}

/**
 * Fexa automation: emitted when the CMMS source quantity changes so that other
 * offerings holding the derived onboarding-price targets can recalculate.
 */
interface UpdateQuoteOfferingFexaData {
  /**
   * Source offering that triggered the event, used to avoid reprocessing on
   * the emitting offering and to check temporal overlap.
   */
  sourceOffering: {
    quoteOfferingId: string;
    parentQuoteOfferingId: string | null;
    startDate: Date;
    endDate: Date;
  };
  /**
   * Quantity of the CMMS source product driving the derived calculations
   */
  cmmsQuantity: number;
  /**
   * Register a target offering's save. The source waits for every registered
   * save before it re-fetches the quote, so one re-fetch shows both offerings.
   * Must be called synchronously while the event is being handled.
   */
  waitUntil: (save: Promise<unknown>) => void;
}

/**
 * Custom-field mirror automation: emitted after an offering holding mirror
 * SOURCE lines is saved so that quantity-locked lines in other offerings that
 * share a mirror match key can follow their quantity 1:1.
 */
interface UpdateQuoteOfferingMirrorQtyData {
  /**
   * Source offering that triggered the event, used to avoid reprocessing on
   * the emitting offering and to check temporal overlap.
   */
  sourceOffering: {
    quoteOfferingId: string;
    parentQuoteOfferingId: string | null;
    startDate: Date;
    endDate: Date;
  };
  /**
   * Quantity of each mirror source line in the saved offering, keyed by its
   * mirror match key
   */
  quantityByMatchKey: Record<string, number>;
}

// Map of event names to their data types
interface CustomEventMap {
  updateQuoteOfferingQty: UpdateQuoteOfferingQtyData;
  updateQuoteOfferingFexaCmms: UpdateQuoteOfferingFexaData;
  updateQuoteOfferingMirrorQty: UpdateQuoteOfferingMirrorQtyData;
}

// Type-safe custom event
type TypedCustomEvent<K extends keyof CustomEventMap> = CustomEvent<
  CustomEventMap[K]
>;

/**
 * Utility function to dispatch typed custom events
 *
 * @param eventName
 * @param data
 */
export function dispatchCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  data: CustomEventMap[K],
) {
  const event = new CustomEvent(eventName, { detail: data });
  window.dispatchEvent(event);
}

/**
 * Hook for listening to custom events
 *
 * @param eventName
 * @param handler
 */
export function useCustomEventListener<K extends keyof CustomEventMap>(
  eventName: K,
  handler: (data: CustomEventMap[K]) => void,
) {
  // Avoid having to have a stable handler passed in by storing in ref on every render
  const savedHandler = useRef(handler);
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const eventListener = (event: Event) => {
      const customEvent = event as TypedCustomEvent<K>;
      savedHandler.current(customEvent.detail);
    };

    window.addEventListener(eventName, eventListener);

    return () => {
      window.removeEventListener(eventName, eventListener);
    };
  }, [eventName]);
}
